import React from 'react'
import { X } from 'lucide-react'
import { NativeModalDialog } from './NativeModalDialog'
import { useCurrency, isSupportedCurrency, PRICE_UNAVAILABLE_LABEL } from '../../../contexts/CurrencyContext'
import styles from '../CreateTransaction.module.css'

export type BasketPreviewItem = {
  productId: string
  quantity: number
  inStock: boolean
  cardName: string
  printing: string
  condition: string
  /** Listed price in USD cents — what the loaded sale line records. */
  priceCents: number
  /** The same listing in the store's currency, as the customer saw it. Null when unpriced. */
  storePriceCents?: number | null
  imageUrl: string | null
  setName: string
  setCode: string
  cardNumber: string
}

export type BasketPreviewData = {
  code: string
  storeName: string | null
  customerName: string | null
  customerEmail: string | null
  customerPhone: string | null
  items: BasketPreviewItem[]
  totalCents: number
  /** Basket total in the store's currency; null when any line has no store price. */
  storeTotalCents?: number | null
  storeCurrency?: string | null
}

export type LoadedBasketSummary = {
  code: string
  itemCount: number
  /** USD cents, as the loaded sale lines record them. */
  totalCents: number
  /** The same lines at their listed store-currency prices; null when any line had none. */
  storeTotalCents: number | null
  storeCurrency: string | null
  customerSummary: string | null
}

type BasketModalProps = {
  open: boolean
  onClose: () => void
  basketCodePrefix: string
  basketCodeInput: string
  onBasketCodeInputChange: (value: string) => void
  onPreviewBasket: () => void
  loadingBasket: boolean
  basketLoadError: string | null
  basketPreview: BasketPreviewData | null
  loadedBasket: LoadedBasketSummary | null
  committingBasket: boolean
  onCommitBasket: () => void
  normalizeBasketCode: (value: string) => string
}

export const BasketModal: React.FC<BasketModalProps> = ({
  open,
  onClose,
  basketCodePrefix,
  basketCodeInput,
  onBasketCodeInputChange,
  onPreviewBasket,
  loadingBasket,
  basketLoadError,
  basketPreview,
  loadedBasket,
  committingBasket,
  onCommitBasket,
  normalizeBasketCode,
}) => {
  const { formatPrice } = useCurrency()
  // Basket prices are quoted in the store's own currency, exactly as the customer's basket page
  // showed them. This used to print the USD figure behind a bare "$".
  const storeCurrency = basketPreview?.storeCurrency?.toUpperCase()
  const fmt = (storeCents: number | null | undefined): string =>
    typeof storeCents === 'number' && isSupportedCurrency(storeCurrency)
      ? formatPrice(storeCents, storeCurrency)
      : PRICE_UNAVAILABLE_LABEL

  return (
  <NativeModalDialog open={open} onClose={onClose} className={styles.nativeModalDialog}>
    <div className={`${styles.modalContent} ${styles.basketModal}`} onClick={(e) => e.stopPropagation()}>
      <div className={styles.modalHeader}>
        <h3 className={styles.modalTitle}>Add Basket to Store Items</h3>
        <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
      </div>

      <div className={styles.modalBody}>
        <div className={styles.basketModalIntro}>
          <p className={styles.basketModalIntroTitle}>Enter the customer&apos;s basket code</p>
          <p className={styles.basketModalIntroText}>
            Enter the 5-character customer code to preview the basket, then load it into Store Items. Basket codes are case-insensitive, customer details auto-fill this transaction, and the basket is deleted once loaded.
          </p>
        </div>

        <div className={styles.basketCodeEntry}>
          <label className={styles.formLabel}>Basket Code</label>
          <div className={styles.basketCodeRow}>
            {basketCodePrefix && <span className={styles.basketCodePrefix}>{basketCodePrefix}</span>}
            <input
              className={styles.basketCodeInput}
              type="text"
              value={
                basketCodePrefix && basketCodeInput.startsWith(basketCodePrefix)
                  ? basketCodeInput.slice(basketCodePrefix.length)
                  : basketCodeInput
              }
              onChange={(e) => onBasketCodeInputChange(normalizeBasketCode(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void onPreviewBasket()
              }}
              placeholder={basketCodePrefix ? 'Enter the 5-character code' : 'e.g. A3B2X'}
              maxLength={15}
              autoCapitalize="characters"
              spellCheck={false}
            />
            <button
              type="button"
              className={styles.loadBasketBtn}
              onClick={() => void onPreviewBasket()}
              disabled={!basketCodeInput.trim() || loadingBasket}
            >
              {loadingBasket ? 'Checking…' : 'Preview Basket'}
            </button>
          </div>
        </div>

        {basketLoadError && <div className={styles.basketModalError}>{basketLoadError}</div>}

        {loadedBasket && (!basketPreview || basketPreview.code !== loadedBasket.code) && (
          <div className={styles.basketQuickError}>
            Basket {loadedBasket.code} is already linked to this transaction. Remove it before adding another basket.
          </div>
        )}

        {basketPreview && (
          <div className={styles.basketPreview}>
            <p className={styles.basketPreviewMeta}>
              {basketPreview.storeName && <strong>{basketPreview.storeName} · </strong>}
              Basket <code>{basketPreview.code}</code>
              {' · '}
              {basketPreview.items.length} item{basketPreview.items.length !== 1 ? 's' : ''}
            </p>
            <div className={styles.basketPreviewHero}>
              <div className={styles.basketPreviewStat}>
                <span className={styles.basketPreviewStatLabel}>Store Items to add</span>
                <strong>{basketPreview.items.filter((i) => i.inStock).reduce((sum, item) => sum + item.quantity, 0)}</strong>
              </div>
              <div className={styles.basketPreviewStat}>
                <span className={styles.basketPreviewStatLabel}>Basket total</span>
                <strong>{fmt(basketPreview.storeTotalCents)}</strong>
              </div>
            </div>
            {(basketPreview.customerName || basketPreview.customerEmail || basketPreview.customerPhone) && (
              <p className={styles.basketPreviewCustomer}>
                👤 {[basketPreview.customerName, basketPreview.customerEmail, basketPreview.customerPhone].filter(Boolean).join(' · ')}{' '}
                <span className={styles.basketPreviewCustomerNote}>(will auto-fill customer info)</span>
              </p>
            )}
            <ul className={styles.basketPreviewList}>
              {basketPreview.items.map((item) => (
                <li
                  key={item.productId}
                  className={`${styles.basketPreviewItem} ${!item.inStock ? styles.basketPreviewItemOut : ''}`}
                >
                  {item.imageUrl && <img src={item.imageUrl} alt={item.cardName} className={styles.basketPreviewImg} />}
                  <div className={styles.basketPreviewInfo}>
                    <span className={styles.basketPreviewName}>{item.cardName}</span>
                    <span className={styles.basketPreviewMeta2}>
                      {item.printing} · {item.condition}
                      {!item.inStock ? ' · ⚠ Out of stock' : ''}
                    </span>
                    <span className={styles.basketPreviewMeta3}>
                      Qty {item.quantity} · {fmt(item.storePriceCents)} each
                    </span>
                  </div>
                  <div className={styles.basketPreviewRight}>
                    <span className={styles.basketPreviewQty}>×{item.quantity}</span>
                    <span className={styles.basketPreviewPrice}>
                      {fmt(typeof item.storePriceCents === 'number' ? item.storePriceCents * item.quantity : null)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <div className={styles.basketPreviewTotal}>
              Total: <strong>{fmt(basketPreview.storeTotalCents)}</strong>
              {basketPreview.items.some((i) => !i.inStock) && (
                <span className={styles.basketPreviewOutNote}> (out-of-stock items will be skipped)</span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={styles.modalActions}>
        <button type="button" className={styles.cancelBtn} onClick={onClose}>
          Cancel
        </button>
        {basketPreview && (
          <button
            type="button"
            className={styles.loadBasketBtn}
            disabled={committingBasket || (!!loadedBasket && loadedBasket.code !== basketPreview.code)}
            onClick={() => void onCommitBasket()}
          >
            {committingBasket
              ? 'Loading…'
              : `Load ${basketPreview.items.filter((i) => i.inStock).length} Item${basketPreview.items.filter((i) => i.inStock).length !== 1 ? 's' : ''}`}
          </button>
        )}
      </div>
    </div>
  </NativeModalDialog>
  )
}
